import { supabase } from './supabaseClient';

const CLOUDINARY_CLOUD_NAME = 'dh9jfogh1';

/**
 * Upload a file to Cloudinary using unsigned upload with preset
 * @param {File} file - The file to upload
 * @param {string} resourceType - The type of resource (image, video, audio, raw)
 * @param {string} folder - The folder to upload to (default: 'stories')
 * @param {string} publicId - Optional public ID for the file
 * @returns {Promise<{url: string, publicId: string}>} - The uploaded file URL and public ID
 */
export const uploadToCloudinary = async (file, resourceType, folder = 'stories', publicId = null) => {
  try {
    console.log('📤 Uploading to Cloudinary:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      resourceType,
      folder
    });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'e8mdddgj'); // Unsigned upload preset from Flutter
    
    if (folder) {
      formData.append('folder', folder);
    }
    if (publicId) {
      formData.append('public_id', publicId);
    }

    const uploadUrl = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${resourceType}/upload`;
    console.log('📍 Upload URL:', uploadUrl);

    // Direct upload to Cloudinary using unsigned preset
    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData
    });

    console.log('📨 Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Upload failed:', errorText);
      try {
        const error = JSON.parse(errorText);
        throw new Error(error.error?.message || 'Upload failed');
      } catch (e) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }
    }

    const data = await response.json();
    console.log('✅ Upload successful:', data.secure_url);

    return {
      url: data.secure_url,
      publicId: data.public_id,
      format: data.format,
      resourceType: data.resource_type,
      bytes: data.bytes,
      duration: data.duration // For audio/video files
    };

  } catch (error) {
    console.error('💥 Error uploading to Cloudinary:', error);
    throw error;
  }
};



/**
 * Upload audio file to Supabase Storage
 * @param {File} audioFile - The audio file to upload
 * @returns {Promise<{url: string, publicId: string, duration: number}>} - The uploaded audio URL and metadata
 */
export const uploadAudio = async (audioFile) => {
  try {
    // Generate unique filename with timestamp
    const timestamp = Date.now();
    const fileExt = audioFile.name.split('.').pop();
    const fileName = `${timestamp}_${audioFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const filePath = fileName;

    console.log('📤 Uploading audio to Supabase Storage:', fileName);

    // Upload to Supabase Storage 'audio' bucket
    const { data, error } = await supabase.storage
      .from('audio')
      .upload(filePath, audioFile, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('❌ Supabase upload error:', error);
      throw error;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('audio')
      .getPublicUrl(filePath);

    console.log('✅ Audio uploaded successfully:', publicUrl);

    return {
      url: publicUrl,
      publicId: filePath,
      format: fileExt,
      resourceType: 'audio',
      bytes: audioFile.size
    };
  } catch (error) {
    console.error('💥 Error uploading audio:', error);
    throw error;
  }
};

/**
 * Upload video file to Supabase Storage
 * @param {File} videoFile - The video file to upload
 * @returns {Promise<{url: string, publicId: string, duration: number}>} - The uploaded video URL and metadata
 */
export const uploadVideo = async (videoFile) => {
  try {
    // Generate unique filename with timestamp
    const timestamp = Date.now();
    const fileExt = videoFile.name.split('.').pop();
    const fileName = `${timestamp}_${videoFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const filePath = fileName;

    console.log('📤 Uploading video to Supabase Storage:', fileName);

    // Upload to Supabase Storage 'video' bucket
    const { data, error } = await supabase.storage
      .from('video')
      .upload(filePath, videoFile, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('❌ Supabase upload error:', error);
      throw error;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('video')
      .getPublicUrl(filePath);

    console.log('✅ Video uploaded successfully:', publicUrl);

    return {
      url: publicUrl,
      publicId: filePath,
      format: fileExt,
      resourceType: 'video',
      bytes: videoFile.size
    };
  } catch (error) {
    console.error('💥 Error uploading video:', error);
    throw error;
  }
};

/**
 * Upload a cover image to Supabase Storage for a channel
 * @param {File} file - The image file to upload
 * @param {number|string} channelId - The channel ID (path prefix required by RLS)
 * @returns {Promise<{url: string, path: string}>}
 */
export const uploadChannelImage = async (file, channelId) => {
  if (!channelId) throw new Error('uploadChannelImage: channelId is required');
  try {
    const timestamp = Date.now();
    const sanitized = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const filePath = `${channelId}/${timestamp}_${sanitized}`;

    const { error } = await supabase.storage
      .from('channel-covers')
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('channel-covers')
      .getPublicUrl(filePath);

    return { url: publicUrl, path: filePath };
  } catch (error) {
    console.error('Error uploading channel cover image:', error);
    throw error;
  }
};

/**
 * Delete a channel cover image from Supabase Storage
 * @param {string} urlOrPath - Full public URL or storage path (e.g. "42/1234_cover.jpg")
 */
export const deleteChannelImage = async (urlOrPath) => {
  if (!urlOrPath) return;
  const raw = urlOrPath.includes('/channel-covers/')
    ? urlOrPath.split('/channel-covers/')[1]
    : urlOrPath;
  const storagePath = raw.split('?')[0];
  const { error } = await supabase.storage
    .from('channel-covers')
    .remove([storagePath]);
  if (error) {
    console.error('Failed to delete channel cover image:', error);
    throw error;
  }
};

/**
 * Get optimized Cloudinary URL for audio/video files
 * @param {string} url - The Cloudinary URL
 * @param {object} options - Transformation options
 * @returns {string} - The optimized URL
 */
export const getOptimizedMediaUrl = (url, options = {}) => {
  if (!url || !url.includes('cloudinary.com')) {
    return url;
  }

  const { quality = 'auto:good', format = 'auto' } = options;
  
  // Apply transformations for audio/video optimization
  const transformations = [
    `q_${quality}`,
    `f_${format}`
  ].join(',');

  if (url.includes('/upload/')) {
    return url.replace('/upload/', `/upload/${transformations}/`);
  }

  return url;
};

/**
 * Validate file type and size for uploads
 * @param {File} file - The file to validate
 * @param {string} resourceType - The expected resource type
 * @returns {object} - Validation result with isValid boolean and error message
 */
export const validateFile = (file, resourceType) => {
  const maxSizes = {
    audio: 50 * 1024 * 1024, // 50MB for audio
    video: 100 * 1024 * 1024, // 100MB for video
    image: 10 * 1024 * 1024, // 10MB for images
    raw: 50 * 1024 * 1024 // 50MB for other files
  };

  const allowedTypes = {
    audio: ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac'],
    video: ['video/mp4', 'video/mov', 'video/avi', 'video/wmv', 'video/flv', 'video/webm'],
    image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
    raw: ['application/pdf', 'text/plain', 'application/msword']
  };

  // Check file size
  if (file.size > maxSizes[resourceType]) {
    return {
      isValid: false,
      error: `File size must be less than ${Math.round(maxSizes[resourceType] / (1024 * 1024))}MB`
    };
  }

  // Check file type
  if (!allowedTypes[resourceType].includes(file.type)) {
    return {
      isValid: false,
      error: `File type not supported. Allowed types: ${allowedTypes[resourceType].join(', ')}`
    };
  }

  return { isValid: true, error: null };
};
